import { FunctionComponent, useCallback } from "react";
import {
  ShopCardProps,
  TabbedCollection,
} from "@bluebottlecoffee/design-system/components";
import {
  Product,
  ProductVariant,
  useAnalytics,
  useCart,
  useProduct,
} from "@chordcommerce/react-autonomy";
import useVariantAvailability from "../lib/chord/hooks/components/use-variant-availability";
import useVariantPrice from "../lib/chord/hooks/components/use-variant-price";
import { ResolvedTabbedCollection } from "../lib/resolve-tabbed-collections";
import { ShopCardPropsWithSlug } from "../lib/transformers/shop-card";
import { toTabbedCollectionProps } from "../lib/transformers/tabbed-collection";

type TabbedCollectionWrapperProps = {
  moduleProps: ResolvedTabbedCollection;
  lang: string;
  region: string;
};

export const TabbedCollectionWrapper: FunctionComponent<
  TabbedCollectionWrapperProps
> = ({ moduleProps, lang, region }) => {
  const { tabProducts, subscriptionOnly } = moduleProps;
  const { addSubscription, addToCart: chordAddToCart } = useCart();
  const { createStockRequest } = useProduct();
  const { trackProductClicked } = useAnalytics();

  const addToCart = useCallback(
    (sku: string, quantity: number) => chordAddToCart({ sku, quantity }),
    [chordAddToCart],
  );

  const handleCreateStockRequest = useCallback(
    async ({ email, sku }, product: Product) => {
      const productData: Product = {
        ...product,
        variant:
          product?.variant?.sku !== sku
            ? ({ sku } as ProductVariant)
            : product?.variant,
      };

      await createStockRequest({ email, product: productData });
    },
    [createStockRequest],
  );

  const withCartHandlers = useCallback(
    (cards: ShopCardPropsWithSlug[]): ShopCardProps[] =>
      cards.map((card) => ({
        ...card,
        subscribeProduct: addSubscription,
        addToCart,
        useVariantAvailability,
        useVariantPrice,
        createStockRequest: (data) =>
          handleCreateStockRequest(data, card.trackShopcardData?.product),
        clickTracking: () => trackProductClicked(card.trackShopcardData),
        ...(subscriptionOnly && {
          product: { ...card.product, subscriptionType: "plan" },
        }),
      })) as ShopCardProps[],
    [
      addSubscription,
      addToCart,
      handleCreateStockRequest,
      trackProductClicked,
      subscriptionOnly,
    ],
  );

  // tabProducts is only missing if something renders this module
  // from data that skipped `resolveTabbedCollections` (build) or
  // `preserveTabbedCollectionProducts` (live preview) — fall back to empty
  // tabs rather than crashing.
  const productsByTab = (tabProducts ?? []).map((cards) =>
    withCartHandlers(cards),
  );

  return (
    <TabbedCollection
      {...toTabbedCollectionProps(moduleProps, region, lang, productsByTab)}
    />
  );
};
